import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link } from 'react-router-dom';
import { 
  Store, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Eye,
  Search,
  RefreshCw,
  AlertCircle,
  Mail,
  Phone,
  Calendar,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { CA_BASE } from '@/utils/getBasePath';
const ca = (route = '') => route ? `${CA_BASE}/${route}` : (CA_BASE || '/');

const API = process.env.REACT_APP_BACKEND_URL || '';
const getCompanyAdminToken = () => {
  return localStorage.getItem('company_admin_token');
};

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-500', icon: Clock },
  reviewed: { label: 'Reviewed', color: 'bg-yellow-500', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
};

export default function OnboardingDashboard() {
  const [counts, setCounts] = useState({ new: 0, reviewed: 0, approved: 0, rejected: 0, total: 0 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchCounts = async () => {
    try {
      const token = getCompanyAdminToken();
      const res = await fetch(`${API}/api/admin/onboarding/restaurants/pending-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCounts(data);
      }
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  };

  const fetchItems = useLatestCallback(async () => {
    setLoading(true);
    try {
      const token = getCompanyAdminToken();
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('q', search);
      
      const res = await fetch(`${API}/api/admin/onboarding/restaurants?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items);
      }
    } catch (err) {
      console.error('Failed to fetch items:', err);
    } finally {
      setLoading(false);
    }
  });

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchCounts(), fetchItems()]);
    setRefreshing(false);
    toast.success('Refreshed');
  };

  useEffect(() => {
    fetchCounts();
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    fetchItems();
  }, [statusFilter, search, fetchItems]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="onboarding-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Onboarding Inbox</h1>
          <p className="text-muted-foreground">Review and manage new restaurant signups</p>
        </div>
        <Button 
          onClick={refresh} 
          variant="outline"
          disabled={refreshing}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(statusConfig).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <Card 
              key={key} 
              className={`cursor-pointer hover:border-primary transition-colors ${statusFilter === key ? 'border-primary ring-2 ring-primary/20' : ''}`}
              onClick={() => setStatusFilter(statusFilter === key ? 'all' : key)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${config.color}/20`}>
                    <Icon className={`w-5 h-5 ${config.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{counts[key]}</p>
                    <p className="text-sm text-muted-foreground">{config.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or restaurant name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <Card className="p-8 text-center">
            <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            <p className="mt-2 text-muted-foreground">Loading...</p>
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">No onboarding items found</p>
          </Card>
        ) : (
          items.map((item) => {
            const statusInfo = statusConfig[item.status] || statusConfig.new;
            const StatusIcon = statusInfo.icon;
            
            return (
              <Link 
                key={item.id} 
                to={`${ca('onboarding')}/${item.id}`}
                className="block"
                data-testid={`onboarding-item-${item.id}`}
              >
                <Card className="hover:border-primary transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${statusInfo.color}/20`}>
                          <Store className={`w-5 h-5 ${statusInfo.color.replace('bg-', 'text-')}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{item.restaurant_name}</h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {item.owner_email}
                            </span>
                            {item.owner_phone_e164 && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {item.owner_phone_e164}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <Badge variant="outline" className={`${statusInfo.color} text-white`}>
                            {statusInfo.label}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(item.created_at)}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })
        )}
      </div>

      {/* Empty State for No New */}
      {!loading && counts.new === 0 && statusFilter === 'all' && items.length === 0 && (
        <Card className="p-12 text-center border-dashed">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
          <h3 className="text-lg font-semibold">All caught up!</h3>
          <p className="text-muted-foreground mt-1">No new restaurant signups to review</p>
        </Card>
      )}
    </div>
  );
}
